import React, { useMemo } from 'react'
import { FormikValues } from 'formik'

import { FormikStepper } from '../../../components/form/wizzard/FormikStepper'
import { useFormDialogContext } from '../../../components/layout/FormDialog'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import prepareFormValues from '../../../utils/prepareFormValues'
import CreatePositionDetailsStep, { summarySchema } from '../../position/createPosition/steps/CreatePositionDetailsStep'
import CreatePositionSettingsStep, {
  settingsSchema,
} from '../../position/createPosition/steps/CreatePositionSettingsStep'
import { buildPositionCreateFormInitialValues } from '../../position/createPosition/buildPositionCreateInitialValues'
import { createPositionDraft, WebsiteCreatePositionDraft } from './websiteCreateDrafts'
import { useUserPermissions } from '../../company/userPermissions'
import getIabForBackend from '../../../utils/getIabForBackend'
import { Iab } from '../../../__generated__/graphql'

interface WebsiteCreatePositionModalProps {
  onAdd: (position: WebsiteCreatePositionDraft) => void
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  publisherCompanyId?: string | number
  defaultIabCategory?: string | Iab | null
}

export const WebsiteCreatePositionModal: React.FC<WebsiteCreatePositionModalProps> = ({
  onAdd,
  setOpen,
  publisherCompanyId,
  defaultIabCategory,
}) => {
  const [, setHasChanged] = useFormDialogContext()
  const t = useAppTranslations()
  const [error, setError] = React.useState(undefined)
  const { superAdmin } = useUserPermissions()

  const initialValues = useMemo(
    () =>
      buildPositionCreateFormInitialValues(
        defaultIabCategory ? (getIabForBackend(defaultIabCategory as string) as Iab) : null
      ),
    [defaultIabCategory]
  )

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    onAdd(createPositionDraft(prepareFormValues(values) as Omit<WebsiteCreatePositionDraft, 'draftId'>))
  }

  return (
    <FormikStepper
      initialValues={initialValues}
      onSubmit={handleSubmit}
      error={error}
      setError={setError}
      enableReinitialize={true}
      successMessage={t('position.created')}
      autoCloseOnSuccess={true}
      setOpen={setOpen}
      setHasChanged={setHasChanged}
      mergeSteps
      validateOnChange={false}
    >
      <CreatePositionDetailsStep
        validationSchema={summarySchema}
        label={t('commons.details')}
        allowMissingWebsite={true}
        publisherCompanyId={publisherCompanyId}
      />
      {superAdmin && <CreatePositionSettingsStep validationSchema={settingsSchema} label={t('commons.settings')} />}
    </FormikStepper>
  )
}
